import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, effect, signal, viewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { ChatThread } from '../../core/services/chat-persistence.service';
import { TooltipComponent } from '../tooltip/tooltip.component';

interface NavigationItem {
  label: string;
  route: string;
  exact?: boolean;
  icon: 'chat' | 'documents' | 'receipt' | 'settings';
}

/**
 * Application-level navigation. State is owned by the shell so this component
 * remains reusable in another layout or host application.
 */
@Component({
  selector: 'app-side-navigation',
  imports: [NgTemplateOutlet, RouterLink, RouterLinkActive, TooltipComponent],
  templateUrl: './side-navigation.component.html',
  styleUrl: './side-navigation.component.scss',
})
export class SideNavigationComponent implements AfterViewInit, OnDestroy {
  @Input() collapsed = false;
  @Input() threads: readonly ChatThread[] = [];
  @Output() collapsedChange = new EventEmitter<boolean>();
  @Output() newChat = new EventEmitter<void>();
  @Output() signOut = new EventEmitter<void>();

  /** Mobile drawer state — only meaningful at the mobile breakpoint. */
  protected readonly mobileOpen = signal(false);
  /** Skills dropdown open state. */
  protected readonly skillsOpen = signal(false);
  /** True when the current route belongs to a skill. */
  protected readonly skillsActive = signal(false);

  private readonly menuButton = viewChild<ElementRef<HTMLButtonElement>>('menuButton');
  private readonly drawer = viewChild<ElementRef<HTMLElement>>('drawer');
  private readonly skillsDropdown = viewChild<ElementRef<HTMLDivElement>>('skillsDropdown');
  private routerEvents?: ReturnType<Router['events']['subscribe']>;
  private readonly onDocumentClick = (event: MouseEvent): void => {
    if (!this.skillsOpen()) return;
    const host = this.skillsDropdown()?.nativeElement;
    if (host && !host.contains(event.target as Node)) {
      this.skillsOpen.set(false);
    }
  };

  constructor(private readonly router: Router) {
    effect(() => {
      if (!this.mobileOpen()) return;
      const drawerEl = this.drawer()?.nativeElement;
      const target = drawerEl?.querySelector<HTMLElement>('a, button');
      (target ?? drawerEl)?.focus();
    });
  }

  protected readonly navigationItems: readonly NavigationItem[] = [
    { label: 'Chat', route: '/chat', icon: 'chat' },
    { label: 'Documents', route: '/documents', icon: 'documents' },
  ];

  protected readonly skillsItems: readonly NavigationItem[] = [
    { label: 'Bill Splitter', route: '/bill-splitter', icon: 'receipt' },
    { label: 'Bills', route: '/bills', icon: 'receipt' },
  ];

  ngAfterViewInit(): void {
    // Close the drawer whenever navigation resolves (a link inside it was used).
    this.routerEvents = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.closeMobileMenu();
        this.closeSkills();
        const url = this.router.url;
        this.skillsActive.set(
          this.skillsItems.some((item) => url === item.route || url.startsWith(`${item.route}/`)),
        );
      }
    });
    // Close the Skills dropdown when clicking outside of it.
    document.addEventListener('click', this.onDocumentClick, true);
  }

  ngOnDestroy(): void {
    this.routerEvents?.unsubscribe();
    document.removeEventListener('click', this.onDocumentClick, true);
    // Release any body scroll lock if the component unmounts while open.
    document.body.style.overflow = '';
  }

  /** Toggles the Skills dropdown open/closed. */
  protected toggleSkills(): void {
    this.skillsOpen.set(!this.skillsOpen());
  }

  /** Closes the Skills dropdown. */
  protected closeSkills(): void {
    this.skillsOpen.set(false);
  }

  /** Tapping the dimmed backdrop (outside the panel) closes the drawer. */
  protected onMenuBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeMobileMenu();
    }
  }

  /** Escape closes the drawer and returns focus to the toggle. */
  protected onMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.skillsOpen()) {
        this.closeSkills();
      }
      if (this.mobileOpen()) {
        this.closeMobileMenu();
        this.menuButton()?.nativeElement.focus();
      }
    }
  }

  protected toggleCollapsed(): void {
    this.collapsedChange.emit(!this.collapsed);
  }

  protected toggleMobileMenu(): void {
    if (this.mobileOpen()) {
      this.closeMobileMenu();
    } else {
      this.mobileOpen.set(true);
      this.lockBodyScroll(true);
    }
  }

  protected closeMobileMenu(): void {
    if (this.mobileOpen()) {
      this.mobileOpen.set(false);
      this.lockBodyScroll(false);
    }
  }

  private lockBodyScroll(locked: boolean): void {
    document.body.style.overflow = locked ? 'hidden' : '';
  }

  /** When collapsed the "S" mark doubles as the expand control. */
  protected onBrandClick(event: Event): void {
    if (this.collapsed) {
      event.preventDefault();
      this.collapsedChange.emit(false);
    }
  }

  protected startNewChat(): void {
    this.newChat.emit();
  }

  protected requestSignOut(): void {
    this.signOut.emit();
  }
}
